import { Link, Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, SearchX, SlidersHorizontal } from 'lucide-react';
import { api, toQueryString } from '@/lib/api';
import { useSeo, useSeoCatalogue, useSeoMakeCatalogue } from '@/hooks/useSeo';
import { CarCard, CarCardSkeleton } from '@/components/CarCard';
import { BrandMark } from '@/components/BrandMark';
import { Button, EmptyState, Spinner } from '@/components/ui';
import { CATEGORY_SEGMENT, SITE_NAME, categoryPath, plural } from '@/lib/seo';
import { formatNumber } from '@/lib/format';
import type { ListingSummary, PagedResult } from '@/lib/types';

/** How many cars a landing page shows before handing over to the search. */
const PREVIEW_SIZE = 24;

function listingCount(count: number): string {
  return `${formatNumber(count)} ${plural(count, 'oglas', 'oglasa', 'oglasa')}`;
}

/**
 * The trail back up the category tree.
 *
 * Present for the visitor, who arrived from a search engine on a page three levels down with no
 * idea what is above it, and for the search engine, which renders this path in place of the raw URL
 * once it has matched it against the BreadcrumbList the server emitted.
 */
function Breadcrumbs({ trail }: { trail: { name: string; to?: string }[] }) {
  return (
    <nav aria-label="Putanja" className="flex flex-wrap items-center gap-1 text-xs text-faint">
      {trail.map((step, index) => (
        <span key={step.name} className="flex items-center gap-1">
          {index > 0 && <ChevronRight size={12} aria-hidden className="shrink-0" />}
          {step.to ? (
            <Link to={step.to} viewTransition className="transition hover:text-accent">
              {step.name}
            </Link>
          ) : (
            <span aria-current="page" className="text-muted">
              {step.name}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}

function Header({
  heading,
  intro,
  trail,
}: {
  heading: string;
  intro: string;
  trail: { name: string; to?: string }[];
}) {
  return (
    <section className="relative overflow-hidden">
      <div className="aurora">
        <span />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 pt-6 pb-6 sm:pt-10">
        <Breadcrumbs trail={trail} />

        {/*
          The one H1 on the page, and it is the phrase the page is meant to be found for. A heading
          that says "Rezultati" on a page targeting "polovni Audi" throws away the single strongest
          on-page signal there is.
        */}
        <h1 className="font-display mt-3 text-3xl leading-tight font-bold text-balance text-body sm:text-4xl">
          {heading}
        </h1>

        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted">{intro}</p>
      </div>
    </section>
  );
}

/** The grid, plus the way out of it into the real search. */
function Results({
  request,
  total,
  searchTo,
}: {
  request: string;
  total: number | undefined;
  searchTo: string;
}) {
  const query = useQuery({
    queryKey: ['listings', request],
    queryFn: () => api<PagedResult<ListingSummary>>(`/api/listings?${request}`),
  });

  const listings = query.data?.items ?? [];
  const found = query.data?.total ?? total ?? 0;

  return (
    <div className="mx-auto max-w-7xl px-4 pb-10">
      {query.isPending ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }, (_, index) => (
            <CarCardSkeleton key={index} index={index} />
          ))}
        </div>
      ) : listings.length === 0 ? (
        <EmptyState
          icon={<SearchX size={40} />}
          title="Trenutno nema oglasa"
          description="Nijedan oglas ne odgovara ovoj kategoriji. Pokušajte sa širom pretragom."
          action={
            <Link to="/" viewTransition>
              <Button variant="secondary">Sve pretrage</Button>
            </Link>
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {listings.map((listing, index) => (
              <CarCard key={listing.id} listing={listing} index={index} />
            ))}
          </div>

          {/*
            A landing page shows a sample and then hands over. Paginating here would multiply every
            category into a dozen near-identical URLs for a crawler to work through, and every one
            of those is budget not spent on an advert.
          */}
          {found > listings.length && (
            <div className="mt-8 flex justify-center">
              <Link to={searchTo} viewTransition>
                <Button variant="secondary" size="lg">
                  <SlidersHorizontal size={16} />
                  Prikaži svih {listingCount(found)}
                </Button>
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** A rail of onward links: the marques from the root, the models from a marque. */
function LinkRail({
  title,
  links,
}: {
  title: string;
  links: { key: string; label: string; count: number; to: string; makeName?: string }[];
}) {
  if (links.length === 0) {
    return null;
  }

  return (
    <section className="mx-auto max-w-7xl px-4 pb-12">
      <h2 className="font-display text-lg font-bold text-body">{title}</h2>

      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {links.map((link) => (
          <Link
            key={link.key}
            to={link.to}
            viewTransition
            className="press flex items-center gap-2.5 rounded-xl border border-default bg-raised px-3 py-2.5 transition hover:border-accent hover:shadow-card"
          >
            {link.makeName && (
              <BrandMark make={{ name: link.makeName }} size="sm" className="shrink-0" />
            )}
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-body">{link.label}</span>
              <span className="block text-[11px] text-faint">{listingCount(link.count)}</span>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

/**
 * `/polovni-automobili` — the top of the category tree.
 *
 * Deliberately not a copy of the search page. It is a directory: the marques, with counts, each
 * linking to its own page. That gives a crawler a flat, complete route into every category from one
 * URL, and gives a visitor arriving on "polovni automobili" something to choose from rather than an
 * undifferentiated grid of whatever was posted most recently.
 */
export function CategoryRootPage() {
  const { data: catalogue } = useSeoCatalogue();

  const total = catalogue?.totalListings ?? 0;

  const heading = 'Polovni automobili u Srbiji';

  const intro =
    total > 0
      ? `Svi polovni automobili na ${SITE_NAME} — ${listingCount(total)} iz cele Srbije. ` +
        'Izaberite marku, uporedite cene i javite se prodavcu direktno. Bez reklama i bez provizije.'
      : `Svi polovni automobili na ${SITE_NAME}, iz cele Srbije. Izaberite marku, uporedite cene ` +
        'i javite se prodavcu direktno. Bez reklama i bez provizije.';

  useSeo({
    title: total > 0
      ? `Polovni automobili u Srbiji — ${listingCount(total)} | ${SITE_NAME}`
      : `Polovni automobili u Srbiji | ${SITE_NAME}`,
    description: intro,
    canonicalPath: categoryPath(),
  });

  return (
    <div>
      <Header heading={heading} intro={intro} trail={[{ name: 'Početna', to: '/' }, { name: heading }]} />

      <LinkRail
        title="Pretražite po marki"
        links={(catalogue?.makes ?? []).map((make) => ({
          key: make.slug,
          label: make.name,
          count: make.listingCount,
          to: categoryPath(make.slug),
          makeName: make.name,
        }))}
      />

      <Results
        request={toQueryString({ pageSize: PREVIEW_SIZE, sort: 0 })}
        total={total}
        searchTo="/"
      />
    </div>
  );
}

/**
 * `/polovni-automobili/:makeSlug` and `/polovni-automobili/:makeSlug/:modelSlug`.
 *
 * One component for both depths because they differ only in how far the filter goes: the heading,
 * the intro, the breadcrumb and the query are all the same shape with one more segment.
 */
export function CategoryPage() {
  const { makeSlug, modelSlug } = useParams();

  const { data, isPending, isError } = useSeoMakeCatalogue(makeSlug);

  const make = data?.make;
  const model = modelSlug
    ? data?.models.find((candidate) => candidate.slug === modelSlug)
    : undefined;

  // A marque nobody sells, or a model spelled wrong, is a URL somebody guessed. Sending it to the
  // search page rather than to a 404 keeps the visitor moving, and the server has already told the
  // crawler the page is noindex.
  const unknown = isError || (!isPending && !make) || (Boolean(modelSlug) && !isPending && !model);

  const count = model?.listingCount ?? make?.listingCount ?? 0;

  const heading = model
    ? `Polovni ${model.makeName} ${model.name}`
    : make
      ? `Polovni ${make.name} automobili`
      : 'Polovni automobili';

  const intro = model
    ? `Polovni ${model.makeName} ${model.name} — ${listingCount(count)} u Srbiji. Pregledajte ` +
      'fotografije, cene i kilometražu i javite se prodavcu bez posrednika.'
    : make
      ? `Polovni ${make.name} u Srbiji — ${listingCount(count)}. Uporedite cene, godište i ` +
        'kilometražu i kontaktirajte prodavca direktno. Bez reklama i bez provizije.'
      : '';

  const filters = model
    ? { modelIds: [model.id], pageSize: PREVIEW_SIZE }
    : make
      ? { makeIds: [make.id], pageSize: PREVIEW_SIZE }
      : { pageSize: PREVIEW_SIZE };

  const searchTo = model
    ? `/?makeIds=${model.makeId}&modelIds=${model.id}`
    : make
      ? `/?makeIds=${make.id}`
      : '/';

  useSeo(
    make
      ? {
          title: `${heading} — ${listingCount(count)} | ${SITE_NAME}`,
          description: intro,
          canonicalPath: categoryPath(make.slug, model?.slug),
        }
      : null,
  );

  if (unknown) {
    return <Navigate to={`/${CATEGORY_SEGMENT}`} replace />;
  }

  if (isPending || !make) {
    return (
      <div className="flex justify-center py-24">
        <Spinner className="size-8 text-accent" />
      </div>
    );
  }

  const trail = [
    { name: 'Početna', to: '/' },
    { name: 'Polovni automobili', to: categoryPath() },
    ...(model
      ? [{ name: make.name, to: categoryPath(make.slug) }, { name: model.name }]
      : [{ name: make.name }]),
  ];

  return (
    <div>
      <Header heading={heading} intro={intro} trail={trail} />

      <Results request={toQueryString(filters)} total={count} searchTo={searchTo} />

      <LinkRail
        title={model ? `Ostali ${make.name} modeli` : `${make.name} modeli`}
        links={(data?.models ?? [])
          .filter((candidate) => candidate.slug !== modelSlug)
          .map((candidate) => ({
            key: candidate.slug,
            label: candidate.name,
            count: candidate.listingCount,
            to: categoryPath(make.slug, candidate.slug),
          }))}
      />
    </div>
  );
}
